import {
  ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperIconComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import { VoiceInputService } from '../../../../../core/services/voice-input.service';
import { ghostCompletion } from '../models/cmdk-ranking';
import type { CmdkItem } from '../models/cmdk.model';
import { CmdkStateService } from '../services/cmdk-state.service';

/**
 * The palette's field: search glyph, prefix pill, ghost completion, the input
 * itself, and an Esc keycap.
 *
 * AI CONTEXT — the two things that are subtler than they look
 *
 * **The ghost.** The completion is a separate element rendered BEHIND a
 * transparent-background input, with the typed portion hidden by
 * `visibility: hidden` so it occupies exactly the right width. Measuring text
 * with `canvas.measureText` and positioning by pixels is the other way to do
 * this, and it drifts the moment the font axis changes — and this app has four
 * of them.
 *
 * **The value binding.** The input renders `state.query()` — the text WITHOUT
 * the prefix trigger — while the keyboard layer edits `state.raw()`. Binding
 * the raw value instead would show `p kat` in a field that also shows a People
 * pill, which is the same fact twice and lets the user delete half of it.
 */
@Component({
  selector: 'im-cmdk-input-row',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperIconComponent, PaperKbdComponent],
  templateUrl: './cmdk-input-row.component.html',
  styleUrl: './cmdk-input-row.component.scss',
  host: { class: 'ph-no-capture' },
})
export class CmdkInputRowComponent {
  private readonly state = inject(CmdkStateService);
  protected readonly voice = inject(VoiceInputService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** The top result, for the ghost completion. */
  readonly topItem = input<CmdkItem | undefined>(undefined);
  /** The listbox this combobox controls, for `aria-controls`. */
  readonly listboxId = input.required<string>();
  /** The DOM id of the selected row, for `aria-activedescendant`. */
  readonly activeDescendantId = input<string | null>(null);
  /** Whether the tenant has AI — changes the placeholder, nothing else. */
  readonly aiEnabled = input(false);

  /** Raised by the mic button. ⌥Space routes through the keymap instead. */
  readonly voiceToggled = output<void>();

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('field');

  protected readonly query = this.state.query;
  protected readonly prefix = this.state.prefix;

  /** The completion offered behind the field, or null. */
  protected readonly ghost = computed(() => {
    // Never both. An autocomplete ghost and a live transcript are two
    // different greyed-out strings competing for the same pixels.
    if (this.voice.listening()) return null;
    return ghostCompletion(this.topItem(), this.state.query());
  });

  protected readonly glyph = computed(() => this.prefix()?.icon ?? 'search');

  protected readonly prefixLabel = computed(() => {
    this.locale.locale();
    const prefix = this.prefix();
    return prefix ? this.transloco.translate(prefix.labelKey) : '';
  });

  /**
   * "Search or Ask" (Nath, 4 Oct: no "Workplace AI" in it) — and plainly
   * "Search Workplace" when the tenant has AI off. Deliberately NOT a
   * time-of-day greeting: that is chat framing on a launcher, and it sets a
   * conversational expectation in the first four words.
   */
  protected readonly placeholder = computed(() => {
    this.locale.locale();
    // While listening the field says so. The placeholder is the only part of
    // the row a screen reader reads on focus, so the listening state has to
    // reach it rather than living only in the mic button's colour.
    if (this.voice.listening()) return this.transloco.translate('shell.cmdk.voice.listening');
    const prefix = this.prefix();
    if (prefix) return this.transloco.translate(prefix.placeholderKey);
    return this.transloco.translate(
      this.aiEnabled() ? 'shell.cmdk.placeholder.default_ai' : 'shell.cmdk.placeholder.default',
    );
  });

  protected readonly micLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate(
      this.voice.listening() ? 'shell.cmdk.voice.stop' : 'shell.cmdk.voice.start',
    );
  });

  /**
   * What the recogniser currently thinks it heard, shown ghosted in the field.
   *
   * Interim results are DISPLAY ONLY — they never reach the query. The
   * recogniser revises continuously ("book a" → "book a quiet" → "book a quiet
   * room"), so committing each revision would fire a search per phoneme and
   * make the list strobe. Only the final transcript commits.
   */
  protected readonly interim = computed(() =>
    this.voice.listening() ? this.voice.interim() : '');

  protected readonly clearPrefixLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.clear_prefix');
  });

  constructor() {
    // Focus follows the palette opening. A raw `focus()` in the same tick as
    // the mount lands before the element is laid out in some browsers, so this
    // waits a frame — but unlike the legacy palette's `setTimeout(50)` it is
    // tied to a frame rather than to a guess about how long layout takes.
    effect(() => {
      if (!this.state.open()) return;
      requestAnimationFrame(() => this.inputRef()?.nativeElement.focus());
    });

    // Keep the DOM value equal to `query()`.
    //
    // This CANNOT be left to `[value]="query()"` alone. Angular writes a
    // property binding only when the bound EXPRESSION changes, and the prefix
    // rules make the expression go stale-equal: type `:` into an empty field
    // and `query()` is '' both before and after, so Angular writes nothing and
    // the `:` the user typed stays visible next to an Apps pill that claims it
    // was consumed. Same for `>` and for the space that completes `g `.
    //
    // Writing it here compares against the DOM rather than against the last
    // bound value, which is the only comparison that can catch that. Guarded
    // so it never fights the user mid-keystroke.
    effect(() => {
      // Depend on `raw`, NOT on `query`. Signals propagate on Object.is
      // inequality, so a computed that produces the same string does not
      // notify — and `query()` is exactly that computed: typing `:` moves
      // `raw` from '' to ':' while `query` stays ''. Tracking `query` here
      // would make this effect suffer the identical staleness it exists to
      // fix, one level up.
      this.state.raw();
      const expected = this.state.query();
      const el = this.inputRef()?.nativeElement;
      if (el && el.value !== expected) el.value = expected;
    });
  }

  /** Focus the field. The palette calls this after a flow pops. */
  focus(): void {
    this.inputRef()?.nativeElement.focus();
  }

  /** True when the caret sits at the very end with nothing selected. */
  caretAtEnd(): boolean {
    const el = this.inputRef()?.nativeElement;
    if (!el) return false;
    return el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
  }

  hasTextSelection(): boolean {
    const el = this.inputRef()?.nativeElement;
    if (!el) return false;
    return el.selectionStart !== el.selectionEnd;
  }

  protected onInput(event: Event): void {
    this.state.setQuery((event.target as HTMLInputElement).value);
  }

  protected onClearPrefix(): void {
    this.state.clearPrefix();
    this.focus();
  }
}
